import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal
} from '@angular/core';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { Router } from '@angular/router';
import { ToastService } from '~console/app/~shared/services/toast.service';
import { filter, finalize, take, tap, Observable } from 'rxjs';
import { AUTH_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OtpData, FormViolation } from '~core/models/shared.models';
import {
  enforceDigitsOnly,
  matchFieldsValidator,
  strongPasswordValidator
} from '~console/app/~shared/utils/validators';
import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage } from '~console/app/~shared/utils/utils';
import { DateService } from '~core/services/date.service';
import { ROUTES } from '~console/app/~shared/constants/routes.constants';

@Component({
  selector: 'forgot-password-form',
  standalone: false,
  templateUrl: './forgot-password-form.component.html',
  styleUrl: './forgot-password-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordFormComponent
  extends BaseAuthComponent
  implements AfterViewInit
{
  private _router = inject(Router);
  private _toastService = inject(ToastService);
  private _dateService = inject(DateService);

  expiresAt: string | null | undefined = null;
  routes = ROUTES;

  resetOtpData$ = this._authFacade.resetOtpData$ as Observable<OtpData | null>;
  isResettingUserPassword$ = this._authFacade.isResettingUserPassword$;
  isResetFormValid$ = this._authFacade.isResetFormValid$;
  isGettingResetOtp$ = this._authFacade.isGettingResetOtp$;

  violations$ = (
    this._globalFacade.formViolations$ as Observable<
      FormViolation[] | undefined
    >
  ).pipe(
    filter(violations => !!violations),
    tap({
      next: violations => {
        violations.forEach(violation => {
          const control = violation.propertyPath.includes('password')
            ? this.password || this.confirmPassword
            : violation.propertyPath.includes('token') ||
                violation.propertyPath.includes('otp')
              ? this.tokenControl
              : violation.propertyPath.includes('email')
                ? this.email
                : null;
          if (control) {
            control.setErrors({ httpError: violation.title });
            this._cdRef.detectChanges();
          }
        });
      }
    })
  );

  canResendOtp = signal(false);

  resetForm = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        strongPasswordValidator
      ]),
      confirmPassword: new FormControl('', Validators.required),
      token: new FormControl('', [
        Validators.required,
        Validators.pattern(/^\d{6}$/)
      ]),
      email: new FormControl('', [Validators.required, Validators.email])
    },
    {
      validators: matchFieldsValidator('password', 'confirmPassword')
    }
  );

  statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.reset.resetFormValues`;

  ngAfterViewInit(): void {
    this._initializeOtpAndEmail();
  }

  private _initializeOtpAndEmail(): void {
    const queryParams = this._globalFacade.routeData?.queryParams;
    const otp = queryParams?.['otp'];
    const expiresAt = queryParams?.['expiresAt'];
    this.expiresAt = expiresAt
      ? this._dateService.instance(expiresAt.replace(' ', '+')).format()
      : null;

    this.tokenControl?.addValidators(() => {
      if (!this.expiresAt) return null;
      return this._dateService
        .instance()
        .isAfter(this._dateService.instance(this.expiresAt))
        ? { expiredOtp: true }
        : null;
    });
    this.tokenControl?.updateValueAndValidity();

    if (otp && this.tokenControl && !this.tokenControl.hasError('expiredOtp')) {
      this.tokenControl.patchValue(otp);
      this._cdRef.detectChanges();
    }

    const emailFromQuery = queryParams?.['email'];
    if (emailFromQuery) {
      this.email?.setValue(emailFromQuery);
      this.email?.disable({ emitEvent: false });
      this._cdRef.detectChanges();
    }

    this.resetOtpData$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(
          (data): data is OtpData =>
            !!data && !!data.receiver && !!data.expiresAt
        ),
        take(1),
        tap(otpData => {
          if (this._isOtpExpired(otpData.expiresAt)) {
            this.tokenControl?.setErrors({ expiredOtp: true });
            return;
          }

          if (!this.email?.value) {
            this.email?.setValue(otpData.receiver);
            this.email?.disable({ emitEvent: false });
            this._cdRef.detectChanges();
          }
        })
      )
      .subscribe();
  }

  private _isOtpExpired(expiresAt: string): boolean {
    const now = this._dateService.instance();
    const expires = this._dateService.instance(expiresAt);
    return now.isAfter(expires);
  }

  onSubmit(): void {
    this.resetForm.disable();
    (this._authFacade.resetUserPassword() as Observable<void>)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._router.navigate([ROUTES.login], { replaceUrl: true });
          },
          error: (error: HttpErrorResponse) => {
            this._loggerService.error('Error in resetUserPassword:', error);
            if (error.status === 401) {
              this.tokenControl?.setErrors({ httpError: 'Code OTP invalide' });
            } else {
              this._toastService.open({
                content: getErrorMessage(error),
                type: 'error'
              });
            }
            this._cdRef.detectChanges();
          }
        }),
        finalize(() => this.resetForm.enable())
      )
      .subscribe();
  }

  onOtpResend(): void {
    if (!this.email?.value) {
      this._toastService.open({
        content: 'Impossible de renvoyer le code. Veuillez réessayer.',
        type: 'error'
      });
      return;
    }

    (this._authFacade.getResetOneTimePassword() as Observable<unknown>)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: responseData => {
            if (typeof responseData !== 'object' || responseData === null) {
              this._loggerService.error('Invalid OTP response:', responseData);
              return;
            }

            const data = responseData as {
              auth?: { reset?: { otpData?: OtpData } };
            };
            const otpData = data.auth?.reset?.otpData || (data as OtpData);

            if (!otpData.receiver) {
              this._loggerService.error(
                'No receiver in resend OTP response:',
                otpData
              );
              this._toastService.open({
                content: 'Impossible de renvoyer le code. Veuillez réessayer.',
                type: 'error'
              });
              this._cdRef.detectChanges();
              return;
            }

            this.canResendOtp.set(false);
            this._toastService.open({
              content: 'Un nouveau code a été envoyé à votre adresse e-mail',
              type: 'success'
            });
            this._cdRef.detectChanges();
          },
          error: (error: HttpErrorResponse) => {
            this._loggerService.error(
              'Error in getResetOneTimePassword (resend):',
              error
            );
            this._toastService.open({
              content: getErrorMessage(error),
              type: 'error'
            });
            this._cdRef.detectChanges();
          }
        })
      )
      .subscribe();
  }

  onOtpKeydown(event: KeyboardEvent): void {
    enforceDigitsOnly(event);
  }

  getCountdownSeconds(expiresAt?: string): number {
    if (!expiresAt) {
      this._loggerService.warn('No expiresAt provided for countdown');
      return 0;
    }
    try {
      const now = this._dateService.instance();
      const expires = this._dateService.instance(expiresAt);

      if (!expires.isValid()) {
        this._loggerService.error('Invalid expiresAt format:', expiresAt);
        return 0;
      }

      const diffInSeconds = expires.diff(now, 'second');
      return Math.max(0, diffInSeconds);
    } catch (e) {
      this._loggerService.error('Error parsing expiresAt:', expiresAt, e);
      return 0;
    }
  }

  get tokenControl(): AbstractControl | null {
    return this.resetForm.get('token');
  }

  get password(): AbstractControl | null {
    return this.resetForm.get('password');
  }

  get confirmPassword(): AbstractControl | null {
    return this.resetForm.get('confirmPassword');
  }

  get email(): AbstractControl | null {
    return this.resetForm.get('email');
  }
}
